import { useEffect, useState } from 'react';
import { CheckCircle, Image, MessageCircle, RefreshCw, Send, X } from 'react-feather';
import { supportAPI } from '../api';
import './Support.css';

const MAX_ATTACHMENTS = 3;
const MAX_ATTACHMENT_BYTES = 2 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif']);

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

async function filesToAttachments(files) {
  const selected = Array.from(files || []);
  if (selected.length > MAX_ATTACHMENTS) {
    throw new Error(`Upload up to ${MAX_ATTACHMENTS} images at a time.`);
  }

  return Promise.all(selected.map(async (file) => {
    if (!ALLOWED_IMAGE_TYPES.has(file.type)) {
      throw new Error('Only PNG, JPG, WEBP, or GIF screenshots are allowed.');
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      throw new Error('Each screenshot must be 2 MB or smaller.');
    }

    return {
      filename: file.name,
      mime_type: file.type,
      size: file.size,
      data_url: await readFileAsDataUrl(file),
    };
  }));
}

function AttachmentPicker({ attachments, onChange, disabled }) {
  return (
    <div className="support-attachments">
      <label className="support-attachment-picker">
        <Image size={16} />
        <span>Add screenshots</span>
        <input
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          multiple
          disabled={disabled}
          onChange={async (e) => {
            try {
              onChange(await filesToAttachments(e.target.files));
            } catch (err) {
              onChange(attachments, err.message);
            } finally {
              e.target.value = '';
            }
          }}
        />
      </label>
      {attachments.length > 0 && (
        <div className="support-attachment-previews">
          {attachments.map((attachment, index) => (
            <div className="support-attachment-preview" key={`${attachment.filename}-${index}`}>
              <img src={attachment.data_url} alt={attachment.filename} />
              <button type="button" onClick={() => onChange(attachments.filter((_, i) => i !== index))}>
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MessageThread({ messages }) {
  return (
    <div className="support-thread">
      {(messages || []).map(message => (
        <div
          className={`support-thread-message ${message.sender_role === 'user' ? 'from-user' : 'from-admin'}`}
          key={message.id}
        >
          <div className="support-thread-meta">
            <strong>{message.sender_role === 'user' ? message.username : 'Support'}</strong>
            <time>{new Date(message.created_at).toLocaleString()}</time>
          </div>
          <p>{message.message}</p>
          {message.attachments?.length ? (
            <div className="support-message-images">
              {message.attachments.map(attachment => (
                <a href={attachment.data_url} target="_blank" rel="noreferrer" key={attachment.id}>
                  <img src={attachment.data_url} alt={attachment.filename} />
                </a>
              ))}
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

export default function AdminSupport() {
  const [tickets, setTickets] = useState([]);
  const [replies, setReplies] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [msg, setMsg] = useState('');
  const [expandedTicket, setExpandedTicket] = useState(null);

  const loadTickets = async () => {
    const res = await supportAPI.adminAll();
    setTickets(res.data.tickets || []);
  };

  useEffect(() => {
    loadTickets().finally(() => setLoading(false));
  }, []);

  const setReply = (ticketId, next) => {
    setReplies(prev => ({
      ...prev,
      [ticketId]: { message: '', attachments: [], ...(prev[ticketId] || {}), ...next },
    }));
  };

  const updateTicket = async (ticket, changes) => {
    setSavingId(ticket.id);
    setMsg('');
    try {
      await supportAPI.adminUpdate(ticket.id, changes);
      setMsg(`Support request #${ticket.id} updated.`);
      await loadTickets();
    } catch (err) {
      setMsg(err.response?.data?.error || 'Failed to update support request.');
    } finally {
      setSavingId(null);
    }
  };

  const sendReply = async (ticketId) => {
    const reply = replies[ticketId] || { message: '', attachments: [] };
    if (!reply.message.trim()) return;

    setSavingId(ticketId);
    setMsg('');
    try {
      await supportAPI.reply(ticketId, {
        message: reply.message.trim(),
        attachments: reply.attachments || [],
      });
      setReplies(prev => ({ ...prev, [ticketId]: { message: '', attachments: [] } }));
      setMsg(`Reply sent for support request #${ticketId}.`);
      await loadTickets();
    } catch (err) {
      setMsg(err.response?.data?.error || 'Failed to send reply.');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div>
      <h1 className="page-title">Support Requests</h1>
      <p className="page-subtitle">{tickets.length} user complaints and requests</p>

      <div className="card">
        <div className="admin-support-toolbar">
          <div className="support-card-title">
            <MessageCircle size={20} />
            <h3>Inbox</h3>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={loadTickets} disabled={loading}>
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        {msg && (
          <div className={`alert ${msg.includes('Failed') ? 'alert-error' : 'alert-success'} support-alert`}>
            <CheckCircle size={18} /> <span>{msg}</span>
          </div>
        )}

        {loading ? (
          <div className="support-loading"><div className="spinner" /></div>
        ) : tickets.length ? (
          <div className="support-ticket-list">
            {tickets.map(ticket => {
              const reply = replies[ticket.id] || { message: '', attachments: [] };
              return (
                <article className="support-ticket" key={ticket.id}>
                  <div className="support-ticket-top" style={{ cursor: 'pointer' }} onClick={() => setExpandedTicket(expandedTicket === ticket.id ? null : ticket.id)}>
                    <div>
                      <strong>#{ticket.id} {ticket.subject}</strong>
                      <div className="admin-support-user">
                        {ticket.username} | {ticket.email}
                      </div>
                    </div>
                    <span className={`badge badge-${ticket.status === 'resolved' ? 'active' : 'pending'}`}>
                      {ticket.status}
                    </span>
                  </div>
                  {expandedTicket === ticket.id && (
                    <>
                      <MessageThread messages={ticket.messages} />
                      <div className="support-reply-box">
                        <textarea
                          className="form-input support-reply-input"
                          value={reply.message}
                          onChange={e => setReply(ticket.id, { message: e.target.value })}
                          placeholder="Reply directly to this user's inbox..."
                          maxLength={5000}
                        />
                        <AttachmentPicker
                          attachments={reply.attachments || []}
                          disabled={savingId === ticket.id}
                          onChange={(attachments, attachmentError) => {
                            if (attachmentError) setMsg(attachmentError);
                            setReply(ticket.id, { attachments });
                          }}
                        />
                        <div className="admin-support-actions">
                          <button
                            className="btn btn-secondary btn-sm"
                            disabled={savingId === ticket.id}
                            onClick={() => updateTicket(ticket, {
                              status: ticket.status === 'resolved' ? 'open' : 'resolved',
                            })}
                          >
                            {ticket.status === 'resolved' ? 'Reopen' : 'Mark Resolved'}
                          </button>
                          <button
                            className="btn btn-primary btn-sm"
                            disabled={savingId === ticket.id || !reply.message.trim()}
                            onClick={() => sendReply(ticket.id)}
                          >
                            <Send size={14} /> Reply
                          </button>
                        </div>
                      </div>
                      <time>{new Date(ticket.created_at).toLocaleString()}</time>
                    </>
                  )}
                </article>
              );
            })}
          </div>
        ) : (
          <p className="support-empty">No support requests yet.</p>
        )}
      </div>
    </div>
  );
}
